import React, { useState, useEffect, useContext } from 'react';
import { ExperienceContext } from '../../contexts/ExperienceContext';
import ConfigureExperience from './ConfigureExperience';
import ChooseTemplate from '../Template/ChooseTemplate';
import ExperienceCode from '../ExperienceCode';
import ErrorAlert from '../UI/Alert/ErrorAlert';
import { createExperience } from '../../utils/APIs';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';
import { useUserContext } from '../../contexts/UserContext';
import Loader from '../UI/Loader/Loader';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { permission } from '../../config/permission';
import { formattedTemplateName, isValidJsonString, transformExperienceJSON, updateHtmlTemplate } from '../../utils/helper';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import Activation from '../ExperienceCode/Activation';
import { ChevronDown, Check, ChevronRight } from "lucide-react";
import { toast } from 'react-toastify';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { resolveUploadFields, hasUploadFields } from '../../utils/uploadHelper';

const CreateExperience = () => {
    const { enums, content } = useContentfulContext();
    const [currentStep, setCurrentStep] = useState('choose-template');
    const [experienceName, setExperienceName] = useState("");
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { userData, checkPermission } = useUserContext();
    const [selectedTemplate, setSelectedTemplate] = useState({});
    const [fieldValues, setFieldValues] = useState();
    const [transformedExperienceJSON, setTransformedExperienceJSON] = useState();
    const [, setOfferId] = useState();
    const navigate = useNavigate();
    const [validationError, setValidationError] = useState("");
    const [dynamicFields, setDynamicFields] = useState({})
    const [code, setCode] = useState();
    const [experienceCreated, setExperienceCreated] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [pendingUploads, setPendingUploads] = useState(new Map());
    const [isUploading, setIsUploading] = useState(false);

    const { rawData, setExperiencesData, experiencesData } = useContext(ExperienceContext);
    const { enablePreviewMode, disablePreviewMode } = usePreviewMode();
    const location = useLocation();
    const { template } = location.state || [];


    /**
     * handleNextFromConfigure
     * Called when user clicks Next on the configure-experience step.
     * Uploads any pending files now so the code view shows CDN URLs.
     */
    const handleNextFromConfigure = async () => {
        if (pendingUploads.size > 0) {
            try {
                setIsUploading(true);
                const resolvedFieldValues = await resolveUploadFields(
                    fieldValues,
                    pendingUploads,
                    selectedTemplate.config_json,
                    userData?.defaultContentful?._id,
                    userData?.token,
                    userData?.selectedOrganization?.org_token
                );
                setFieldValues(resolvedFieldValues);
                setPendingUploads(new Map()); // clear pending after upload
            } catch (err) {
                setError(err?.message || 'Failed to upload asset. Please try again.');
                setIsUploading(false);
                return;
            } finally {
                setIsUploading(false);
            }
        }
        setCurrentStep('create-offer');
    };

    useEffect(() => {
        if (template && Object.keys(template)?.length > 0) {
            setSelectedTemplate(template);
        }
    }, [])


    useEffect(() => {
        const timer = setTimeout(() => {
            setError('');
        }, 4000);

        return () => clearTimeout(timer);
    }, [error]);

    useEffect(() => {
        if (Object.keys(selectedTemplate).length > 0) {
            setFieldValues(() => {
                const base_json = {};
                JSON.parse(selectedTemplate.config_json).forEach(function (item, index) {
                    const key = item.exp_json_field;
                    base_json[key] = item.input.input_default_value;

                    if (item.input.input_type === "dynamic-text") {
                        setDynamicFields((prev) => ({
                            ...prev,
                            [key]: item.input.dynamic_field
                        }))
                    }
                })
                return base_json;
            });
        }
        if (currentStep === 'choose-template' && selectedTemplate._id) {
            setCurrentStep('configure-experience');
            setExperienceName(`${formattedTemplateName(selectedTemplate.name)}-${new Date().getTime()}`);
        }
    }, [selectedTemplate]);

    const createExperienceHandler = async (callback) => {
        try {
            if (!experienceName.trim()) {
                setError(content.CREATE_EXPERIENCE.exp_name_required);
                return;
            }

            if (validationError) return;
            const isValidate = handleValidateJson();
            if (isValidate.length > 0) return;

            setLoading(true);

            const template = rawData.find((item) => {
                return item.name == selectedTemplate.name;
            })

            // fieldValues already have CDN URLs (resolved on Next click)
            const updatedHtml = updateHtmlTemplate(template.html, fieldValues)
            const payload = {
                name: experienceName,
                templates: [template._id],
                html: '',
                css: '',
                config_json: '',
                experience_json: template.type === enums.templateTypes.json_a_to_html.id ? updatedHtml : JSON.stringify(transformExperienceJSON(fieldValues, dynamicFields)),
                source: enums.experience_sources.stride.id,
            }

            // Save which Contentful integration was used (for future asset operations)
            if (hasUploadFields(selectedTemplate.config_json, 'contentful') && userData?.defaultContentful?._id) {
                payload.upload_contentful_integration_id = userData?.defaultContentful?._id;
            }
            const result = await createExperience(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                payload,
                {
                    event_name: ANALYTICS_EVENTS.CREATE_EXPERIENCE,
                    metadata: { experience_name: experienceName, template_name: selectedTemplate?.name, source: 'stride', created: true }
                }
            );
            const newExperiences = [...experiencesData];
            if (Array.isArray(result.data) && result.data.length) {
                newExperiences.push(result.data[0]);
                setCode(result.data[0].experience_json);
                setExperiencesData(newExperiences);
                setCurrentStep("activation")
                setExperienceCreated(result.data[0])
                toast.success(content.CREATE_EXPERIENCE.success)
                if (!checkPermission(permission.CREATE_EXP_OP_ACTIVATE)) {
                    navigate('/experience/manage-experiences');
                }
                if (typeof callback === "function") {
                    callback();
                }
                // navigate('/experience/manage-experiences');
            }
        } catch (error) {
            setError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setLoading(false);
        }
    }

    const handleValidateJson = () => {
        setValidationError("");
        const isValidJson = isValidJsonString(selectedTemplate.config_json)
        if (!isValidJson) {
            setError(content.CREATE_EXPERIENCE.invalid_json);
            return false;
        }

        const config_elems = [];
        JSON.parse(selectedTemplate.config_json).forEach((el) => {
            el.error = "";
            config_elems.push({ ...el, id: el.id });
            // uncomment below line and remove above line if create experience is not working
            // config_elems.push({ ...el, id: el.id.replace("[", ".").replace("]", "") });
        });
        const newConfigElems = [...config_elems];
        const errs = [];

        for (let item of newConfigElems) {
            let val = "";
            if (dynamicFields[item.id] !== undefined) {
                val = dynamicFields[item.id]
            } else {
                val = fieldValues[item.id];
            }

            const rules = item.rules;
            if (rules.is_mandatory) {
                // For upload fields: a pending file counts as a value
                const hasPendingFile = item.input?.input_type === 'upload' && pendingUploads.has(item.exp_json_field || item.id);
                if (!hasPendingFile && val?.length == 0) {
                    const message = "Field is required";
                    item.error = message;
                    errs.push(`${item.id}: ${message}`)
                } else {
                    item.error = "";
                }
            }
            if (rules.min_length && rules.max_length && item.error?.length == 0) {
                if (val?.length > rules.max_length || val?.length < rules.min_length) {
                    const message = `Min length: ${rules.min_length} and Max length: ${rules.max_length}`;
                    item.error = message;
                    errs.push(`${item.id}: ${message}`);
                } else {
                    item.error = "";
                }
            }
        }

        if (errs?.length > 0) {
            setValidationError(errs.join(", \n"));
        }
        return errs;
    }

    useEffect(() => {
        if (currentStep == "configure-experience") {
            enablePreviewMode();
        } else {
            disablePreviewMode();
        }
    }, [currentStep])

    return (
        <div className=''>
            {loading && <Loader />}
            {error && <ErrorAlert timeout="3000">{error}</ErrorAlert>}

            <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-border shadow-xs px-4 md:px-8 py-3 mb-4 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
                <div className="w-full md:max-w-[260px]">
                    {currentStep !== 'choose-template' && (
                        <Input
                            size="sm"
                            disabled={currentStep === 'activation'}
                            id="experience_name"
                            name="experience_name"
                            placeholder={content.CREATE_EXPERIENCE.exp_name}
                            value={experienceName}
                            onChange={(e) => {
                                if (currentStep === 'activation') return;
                                setExperienceName(e.target.value);
                            }}
                        />
                    )}
                </div>

                <nav aria-label="Progress" className="flex items-center justify-center flex-1 gap-1.5 md:gap-2 flex-wrap">
                    {checkPermission(permission.CREATE_EXP_OP_CHOOSE_TEMPLATE) && (
                        <button
                            type="button"
                            onClick={() => {
                                setCurrentStep('choose-template');
                                setSelectedTemplate({});
                                setFieldValues({});
                            }}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                                currentStep === 'choose-template'
                                    ? 'bg-primary text-white font-semibold shadow-xs'
                                    : 'bg-emerald-50 text-emerald-700 font-medium hover:bg-emerald-100 border border-emerald-200/60'
                            }`}
                        >
                            {currentStep !== 'choose-template' ? (
                                <Check className="w-3.5 h-3.5" />
                            ) : (
                                <span className="w-4 h-4 rounded-full bg-white/20 text-white flex items-center justify-center text-[10px] font-bold">1</span>
                            )}
                            <span>{content.CREATE_EXPERIENCE.choose_template}</span>
                        </button>
                    )}

                    <ChevronRight className="w-3.5 h-3.5 text-stride-muted-light hidden sm:inline-block" />

                    {checkPermission(permission.CREATE_EXP_OP_CONFIGURE_EXPERIENCE) && (
                        <button
                            type="button"
                            disabled={!selectedTemplate?._id}
                            onClick={() => {
                                if (Object?.keys(selectedTemplate)?.length > 0) {
                                    setCurrentStep('configure-experience');
                                }
                            }}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                currentStep === 'configure-experience'
                                    ? 'bg-primary text-white font-semibold shadow-xs'
                                    : (currentStep === 'create-offer' || currentStep === 'activation')
                                        ? 'bg-emerald-50 text-emerald-700 font-medium hover:bg-emerald-100 border border-emerald-200/60'
                                        : 'bg-surface-alt text-stride-muted font-medium border border-border-subtle'
                            }`}
                        >
                            {(currentStep === 'create-offer' || currentStep === 'activation') ? (
                                <Check className="w-3.5 h-3.5" />
                            ) : (
                                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${currentStep === 'configure-experience' ? 'bg-white/20 text-white' : 'bg-border text-stride-muted'}`}>2</span>
                            )}
                            <span>{content.CREATE_EXPERIENCE.config_exp}</span>
                        </button>
                    )}

                    <ChevronRight className="w-3.5 h-3.5 text-stride-muted-light hidden sm:inline-block" />

                    {checkPermission(permission.CREATE_EXP_OP_CREATE_EXPERIENCE) && (
                        <button
                            type="button"
                            disabled={!selectedTemplate?._id}
                            onClick={() => {
                                if (Object?.keys(selectedTemplate)?.length > 0) {
                                    if (currentStep !== 'configure-experience' && currentStep !== 'create-offer') {
                                        setCurrentStep('create-offer');
                                    }
                                }
                            }}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                currentStep === 'create-offer'
                                    ? 'bg-primary text-white font-semibold shadow-xs'
                                    : currentStep === 'activation'
                                        ? 'bg-emerald-50 text-emerald-700 font-medium hover:bg-emerald-100 border border-emerald-200/60'
                                        : 'bg-surface-alt text-stride-muted font-medium border border-border-subtle'
                            }`}
                        >
                            {currentStep === 'activation' ? (
                                <Check className="w-3.5 h-3.5" />
                            ) : (
                                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${currentStep === 'create-offer' ? 'bg-white/20 text-white' : 'bg-border text-stride-muted'}`}>3</span>
                            )}
                            <span>{content.CREATE_EXPERIENCE.create_exp}</span>
                        </button>
                    )}

                    <ChevronRight className="w-3.5 h-3.5 text-stride-muted-light hidden sm:inline-block" />

                    {checkPermission(permission.CREATE_EXP_OP_ACTIVATE) && (
                        <button
                            type="button"
                            disabled={!experienceCreated}
                            onClick={() => {
                                if (Object?.keys(selectedTemplate)?.length > 0 && experienceCreated) {
                                    setCurrentStep('activation');
                                }
                            }}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                currentStep === 'activation'
                                    ? 'bg-primary text-white font-semibold shadow-xs'
                                    : 'bg-surface-alt text-stride-muted font-medium border border-border-subtle'
                            }`}
                        >
                            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${currentStep === 'activation' ? 'bg-white/20 text-white' : 'bg-border text-stride-muted'}`}>4</span>
                            <span>{content.CREATE_EXPERIENCE.activate_exp}</span>
                        </button>
                    )}
                </nav>

                <div className="flex items-center justify-end gap-2.5 w-full md:w-auto">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate("/experience/manage-experiences")}
                    >
                        {content.EDIT_EXPERIENCE.cancel_btn}
                    </Button>
                    {(currentStep !== 'choose-template' && currentStep !== 'activation') && (
                        <div className="flex items-center">
                            <Button
                                id={currentStep === 'create-offer' ? 'save-as-experience-btn-id' : 'go-to-next-step-btn-id'}
                                variant="primary"
                                size="sm"
                                disabled={isUploading || loading}
                                className={currentStep === 'create-offer' && checkPermission(permission.CREATE_EXP_OP_ACTIVATE) ? 'rounded-r-none' : ''}
                                onClick={() => {
                                    setValidationError("");
                                    if (currentStep === 'choose-template' && !selectedTemplate._id) {
                                        setError('Please choose a template');
                                        return;
                                    }
                                    if (currentStep === 'choose-template') {
                                        setExperienceName(`${selectedTemplate.name} - ${new Date().getTime()}`);
                                        setCurrentStep('configure-experience');
                                    } else if (currentStep === 'configure-experience') {
                                        handleNextFromConfigure();
                                    } else if (currentStep === 'create-offer') {
                                        createExperienceHandler(() => {
                                            if (!checkPermission(permission.CREATE_EXP_OP_ACTIVATE)) {
                                                navigate('/experience/manage-experiences');
                                            }
                                        });
                                    }
                                }}
                            >
                                {isUploading ? (
                                    <span className="flex items-center gap-1.5">
                                        <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Uploading…
                                    </span>
                                ) : currentStep === 'create-offer' ? (
                                    checkPermission(permission.CREATE_EXP_OP_ACTIVATE)
                                        ? content.CREATE_EXPERIENCE.save_and_approve
                                        : content.CREATE_EXPERIENCE.save_and_close
                                ) : (
                                    content.CREATE_EXPERIENCE.next_btn
                                )}
                            </Button>
                            {currentStep === 'create-offer' && checkPermission(permission.CREATE_EXP_OP_ACTIVATE) && (
                                <div className="relative">
                                    <button
                                        type="button"
                                        data-testid="create-exp-down-arrow"
                                        onClick={() => setIsOpen(!isOpen)}
                                        className="h-8 px-2 bg-primary hover:bg-primary-hover text-white rounded-r-lg border-l border-primary-hover flex items-center justify-center transition-colors"
                                    >
                                        <ChevronDown className="w-4 h-4" />
                                    </button>
                                    {isOpen && (
                                        <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-dropdown border border-border py-1 z-50">
                                            <button
                                                type="button"
                                                className="w-full text-left px-3 py-2 text-xs font-medium text-stride-heading hover:bg-surface-alt transition-colors"
                                                onClick={() => {
                                                    setIsOpen(false);
                                                    setValidationError("");
                                                    createExperienceHandler(() => {
                                                        navigate('/experience/manage-experiences');
                                                    });
                                                }}
                                            >
                                                {content.CREATE_EXPERIENCE.save_and_close}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </header>
            <div className='h-full px-2 md:px-8 max-h-[calc(100vh-80px)'>
                {checkPermission(permission.CREATE_EXP_OP_CHOOSE_TEMPLATE) && currentStep == 'choose-template' && <ChooseTemplate setCurrentStep={setCurrentStep} selectedTemplate={selectedTemplate} setSelectedTemplate={setSelectedTemplate} />}
                {currentStep == 'configure-experience' &&
                    <ConfigureExperience
                        actionText={"Add experience"}
                        setCurrentStep={setCurrentStep}
                        selectedTemplate={selectedTemplate}
                        setSelectedTemplate={setSelectedTemplate}
                        fieldValues={fieldValues}
                        setFieldValues={setFieldValues}
                        transformedExperienceJSON={transformedExperienceJSON}
                        setTransformedExperienceJSON={setTransformedExperienceJSON}
                        dynamicFields={dynamicFields}
                        setDynamicFields={setDynamicFields}
                        isCreateExperience={true}
                        pendingUploads={pendingUploads}
                        setPendingUploads={setPendingUploads}
                    />
                }
                {currentStep == 'create-offer' &&
                    <ExperienceCode
                        code={code}
                        setCode={setCode}
                        experience={experienceCreated}
                        selectedTemplate={selectedTemplate}
                        experienceJSON={transformedExperienceJSON}
                        permissionId={permission.CREATE_EXP_OP_EDIT_EXP_CODE}
                        setFieldValues={setFieldValues}
                        setValidationError={setValidationError}
                        validationError={validationError}
                        fieldValues={fieldValues}
                        dynamicFields={dynamicFields}
                        setDynamicFields={setDynamicFields}
                    />
                }
                {currentStep == 'activation' &&
                    <Activation
                        code={code}
                        experience={experienceCreated}
                        setExperience={setExperienceCreated}
                        selectedTemplate={selectedTemplate}
                        setOfferId={setOfferId}
                        aemPermission={permission.CREATE_EXP_OP_UPDATE_AEM}
                        setValidationError={setValidationError}
                        validationError={validationError}
                        handleValidateJson={handleValidateJson}
                        offerPermission={{
                            create: permission.CREATE_EXP_OP_CREATE_OFFER,
                            edit: permission.CREATE_EXP_OP_EDIT_OFFER
                        }}
                        contentfulPermission={{
                            create: permission?.CREATE_EXP_OP_CREATE_CONTENTFUL
                        }}
                        tealiumPermission={{
                            create: permission.CREATE_EXP_OP_CREATE_TEALIUM,
                            edit: permission.CREATE_EXP_OP_UPDATE_TEALIUM
                        }}
                    />
                }
            </div>
        </div>
    )
}

export default CreateExperience;